// src/components/Talleres/FormularioInscripcion/PasoConfirmacion.jsx
import React from 'react';
import { Alert, Card, Descriptions, Tag, Space, Button, Divider, Form } from 'antd';
import {
  UserOutlined, TeamOutlined, GiftOutlined, ThunderboltOutlined,
  CheckCircleOutlined, ShoppingCartOutlined, IdcardOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const getCategoriaEdad = (calcularEdad, fechaNacimiento) => {
  const edad = calcularEdad(fechaNacimiento);
  if (edad === null) return { texto: 'Sin edad', color: 'gray' };
  if (edad < 18) return { texto: 'Menor', color: 'blue' };
  else if (edad >= 60) return { texto: 'Adulto Mayor', color: 'purple' };
  else return { texto: 'Adulto', color: 'blue' };
};

const MostrarHorarioConBloques = ({ horario }) => {
  const nombre = horario.nombre ? `${horario.nombre}: ` : '';
  if (horario.bloques && horario.bloques.length > 0) {
    return (
      <div className="space-y-1">
        {nombre && <div className="text-xs font-semibold text-gray-600 mb-1">{nombre}</div>}
        {horario.bloques.map((bloque, bloqueIdx) => (
          <div key={bloque._id || bloque.id || bloqueIdx} className="flex items-center gap-1 flex-wrap">
            {bloque.dias.map((dia, diaIdx) => (
              <Tag key={`${bloque.id}-${dia}-${diaIdx}`} color="blue" className="capitalize text-xs m-0">
                {dia}
              </Tag>
            ))}
            <Tag color="blue" icon={<ClockCircleOutlined />} className="text-xs m-0">
              {bloque.inicio} - {bloque.fin}
            </Tag>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <Tag color="blue" className="capitalize text-xs">{horario.dia || horario.dias?.join(', ')}</Tag>
      <Tag color="blue" icon={<ClockCircleOutlined />} className="text-xs">
        {horario.inicio} - {horario.fin}
      </Tag>
    </div>
  );
};

export default function PasoConfirmacion({
  form,
  taller,
  esGratis,
  user,
  familiares,
  selectedHorarios,
  selectedPlan,
  tipoInscripcion,
  selectedFamiliar,
  preciosAplicados,
  calcularEdad,
  loading,
  esVecino,
  estadoVecino,
  validandoTV,
  onSubmit,
  onPrev
}) {

  const pa = (!esGratis && selectedPlan) ? preciosAplicados[selectedPlan] : null;
  const n  = selectedHorarios.length;

  const totales = pa ? {
    subtotal:             pa.montoOriginal             * n,
    descuentoAuto:        pa.totalDescuentoAutomatico  * n,
    descuentoVecino:      pa.tieneDescuentoVecino ? pa.descuentoEspecial * n : 0,
    total:                pa.montoFinal               * n,
    tieneDescuento:       pa.tieneDescuento,
    descuentosAplicados:  pa.descuentosAplicados || [],
    esAdultoMayor:        pa.esAdultoMayor,
    tieneValorAdultoMayor: pa.tipo === 'adultoMayor',
    tipoPrecio:           pa.tipo,
  } : {
    subtotal: 0, descuentoAuto: 0, descuentoVecino: 0,
    total: 0, tieneDescuento: false, esAdultoMayor: false,
    tieneValorAdultoMayor: false, descuentosAplicados: [], tipoPrecio: 'particular'
  };

  const situacionVecino = (() => {
    if (esGratis || !esVecino || !pa) return null;
    if (pa.tipo === 'adultoMayor')    return 'adultoMayorConVecino';
    if (pa.tieneDescuentoVecino)      return 'vecinoConDescuento';
    return 'vecinoSinDescuento';
  })();

  const precioParticular = pa ? pa.montoOriginal * n : 0;
  const ahorroTotal      = pa ? (pa.montoOriginal - pa.montoFinal) * n : 0;
  const tieneAhorro      = ahorroTotal > 0;

  return (
    <div>
      <Alert
        message="Revisa los datos antes de agregar al carrito"
        description="Los descuentos mostrados son los que se aplicarán según la persona seleccionada"
        type="warning"
        showIcon
        className="mb-4"
      />

      {/* Card resumen principal */}
      <Card title="Resumen de inscripción" className="mb-4">
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label="Taller">
            <strong>{taller.nombre}</strong>
          </Descriptions.Item>

          <Descriptions.Item label="Horarios seleccionados">
            <div className="space-y-2">
              {selectedHorarios.map((horario, idx) => (
                <div key={horario._id || idx} className="border-b border-gray-100 pb-2 last:border-0 last:pb-0">
                  <MostrarHorarioConBloques horario={horario} />
                </div>
              ))}
            </div>
          </Descriptions.Item>

          {!esGratis && (
            <Descriptions.Item label="Plan">
              <Space>
                <Tag color="blue" className="capitalize">{selectedPlan}</Tag>
                {totales.esAdultoMayor && totales.tieneValorAdultoMayor && (
                  <Tag color="purple" icon={<UserOutlined />}>ADULTO MAYOR</Tag>
                )}
                {totales.descuentoAuto > 0 && (
                  <Tag color="orange" icon={<ThunderboltOutlined />}>CON DCTO AUTO</Tag>
                )}
                {totales.descuentoVecino > 0 && (
                  <Tag color="blue" icon={<IdcardOutlined />}>CON DCTO VECINO</Tag>
                )}
              </Space>
            </Descriptions.Item>
          )}

          <Descriptions.Item label="Inscrito">
            {tipoInscripcion === 'para_mi' ? (
              <Space direction="vertical" size="small">
                <div className="flex items-center gap-2 flex-wrap">
                  <Tag color="blue"><UserOutlined /> {user?.name} (Yo)</Tag>
                  {esVecino && <Tag color="blue" icon={<IdcardOutlined />}>VECINO VIGENTE</Tag>}
                </div>
                {user?.birthdate && (
                  <Tag color={getCategoriaEdad(calcularEdad, user.birthdate).color}>
                    {calcularEdad(user.birthdate)} años — {getCategoriaEdad(calcularEdad, user.birthdate).texto}
                  </Tag>
                )}
              </Space>
            ) : (
              <Space direction="vertical" size="small">
                <div className="flex items-center gap-2 flex-wrap">
                  <Tag color="purple">
                    <TeamOutlined /> {selectedFamiliar
                      ? familiares.find(f => f._id === selectedFamiliar)?.nombre
                      : form.getFieldValue('nombreFamiliar')}
                  </Tag>
                  {esVecino && <Tag color="blue" icon={<IdcardOutlined />}>VECINO VIGENTE</Tag>}
                </div>
                {(() => {
                  const fechaNac = selectedFamiliar
                    ? familiares.find(f => f._id === selectedFamiliar)?.fechaNacimiento
                    : form.getFieldValue('fechaNacimientoFamiliar');
                  const cat = getCategoriaEdad(calcularEdad, fechaNac);
                  return (
                    <Tag color={cat.color}>
                      {calcularEdad(fechaNac)} años — {cat.texto}
                    </Tag>
                  );
                })()}
              </Space>
            )}
          </Descriptions.Item>

          {/* Desglose de precios — solo si hay algo que mostrar */}
          {!esGratis && (tieneAhorro || totales.descuentoAuto > 0) && (
            <>
              <Descriptions.Item label="Precio base (particular)">
                <span className="text-gray-400 line-through">
                  ${precioParticular.toLocaleString('es-CL')}
                </span>
              </Descriptions.Item>

              {totales.esAdultoMayor && totales.tieneValorAdultoMayor && (
                <Descriptions.Item label={
                  <span className="flex items-center gap-1 text-purple-600">
                    <UserOutlined /> Tarifa adulto mayor
                  </span>
                }>
                  <span className="text-purple-600 font-semibold">
                    ${totales.subtotal.toLocaleString('es-CL')}
                  </span>
                </Descriptions.Item>
              )}

              {totales.descuentoAuto > 0 && (
                <Descriptions.Item label={
                  <span className="flex items-center gap-1 text-orange-600">
                    <ThunderboltOutlined /> Descuentos automáticos
                  </span>
                }>
                  <span className="text-orange-600 font-semibold">
                    -${totales.descuentoAuto.toLocaleString('es-CL')}
                  </span>
                </Descriptions.Item>
              )}

              {totales.descuentoVecino > 0 && (
                <Descriptions.Item label={
                  <span className="flex items-center gap-1 text-blue-600">
                    <IdcardOutlined /> Descuento vecino
                  </span>
                }>
                  <span className="text-blue-600 font-semibold">
                    -${totales.descuentoVecino.toLocaleString('es-CL')}
                  </span>
                </Descriptions.Item>
              )}
            </>
          )}

          <Descriptions.Item label="Total a pagar">
            {esGratis ? (
              <Tag color="blue" icon={<GiftOutlined />} className="text-xl">
                Taller Gratuito — $0
              </Tag>
            ) : (
              <span className={`text-2xl font-bold ${
                totales.esAdultoMayor && totales.tieneValorAdultoMayor ? 'text-purple-600' :
                totales.tieneDescuento ? 'text-orange-600' :
                'text-blue-600'
              }`}>
                ${totales.total.toLocaleString('es-CL')}
              </span>
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* ── Cards informativas situacionales (solo una se muestra a la vez) ── */}

      {/* CASO: Adulto mayor con vecino → tarifa adulto mayor, sin descuento vecino extra */}
      {situacionVecino === 'adultoMayorConVecino' && (
        <Card className="mb-4 bg-purple-50 border-purple-200">
          <div className="flex items-center gap-2 mb-2">
            <UserOutlined className="text-purple-500" />
            <IdcardOutlined className="text-blue-500" />
            <span className="font-semibold text-purple-700">
              Tarifa Adulto Mayor aplicada
            </span>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            Tienes Tarjeta Vecino vigente y cumples con la edad de adulto mayor (60+ años).
            Se aplica la <strong>tarifa adulto mayor</strong> como precio final.
            La Tarjeta Vecino no suma descuento adicional sobre esta tarifa.
          </p>
          <div className="flex justify-between text-sm bg-white rounded px-3 py-2">
            <span className="text-gray-500">Tarifa aplicada</span>
            <span className="font-bold text-purple-700">
              ${totales.total.toLocaleString('es-CL')}
            </span>
          </div>
          {tieneAhorro && (
            <div className="flex justify-between text-sm bg-white rounded px-3 py-2 mt-1">
              <span className="text-gray-500">Ahorro vs precio particular</span>
              <span className="font-bold text-blue-600">
                -${ahorroTotal.toLocaleString('es-CL')}
              </span>
            </div>
          )}
        </Card>
      )}

      {/* CASO: Vecino con descuento aplicado (no adulto mayor) */}
      {situacionVecino === 'vecinoConDescuento' && (
        <Card className="mb-4 bg-blue-50 border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <IdcardOutlined className="text-blue-500" />
            <span className="font-semibold text-blue-700">Descuento Tarjeta Vecino aplicado</span>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            Se ha aplicado el precio especial para beneficiarios de Tarjeta Vecino.
          </p>
          <div className="flex justify-between text-sm bg-white rounded px-3 py-2">
            <span className="text-gray-500">Descuento aplicado</span>
            <span className="font-bold text-blue-700">
              -${totales.descuentoVecino.toLocaleString('es-CL')}
            </span>
          </div>
          {estadoVecino?.mensaje && (
            <div className="text-xs text-gray-400 mt-2">
              Estado: <strong>{estadoVecino.mensaje}</strong>
            </div>
          )}
        </Card>
      )}

      {/* CASO: Vecino pero este taller no tiene descuento vecino */}
      {situacionVecino === 'vecinoSinDescuento' && (
        <Card className="mb-4 bg-blue-50 border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <IdcardOutlined className="text-blue-500" />
            <span className="font-semibold text-blue-700">Tarjeta Vecino</span>
          </div>
          <p className="text-sm text-gray-700">
            Eres beneficiario de Tarjeta Vecino, sin embargo este taller no tiene
            descuento especial para vecinos.
          </p>
        </Card>
      )}

      {/* ── Card descuentos automáticos ── */}
      {!esGratis && totales.descuentoAuto > 0 && totales.descuentosAplicados.length > 0 && (
        <Card className="mb-4 bg-orange-50 border-orange-200">
          <div className="flex items-center gap-2 mb-3">
            <ThunderboltOutlined className="text-orange-500" />
            <span className="font-semibold text-orange-700">Descuentos automáticos aplicados:</span>
          </div>
          <div className="space-y-2">
            {totales.descuentosAplicados.map((desc, idx) => (
              <div key={idx} className="flex justify-between items-center text-sm">
                <span className="text-gray-700">
                  <strong>{desc.nombre}</strong>
                  {desc.tipo === 'porcentaje' && ` (${desc.valor}%)`}
                  {desc.tipo === 'monto_fijo' && ` ($${desc.valor.toLocaleString('es-CL')})`}
                </span>
                <span className="text-orange-600 font-semibold">
                  -${(desc.montoDescontado * selectedHorarios.length).toLocaleString('es-CL')}
                </span>
              </div>
            ))}
          </div>
          <Divider className="my-2" />
          <div className="text-xs text-gray-600">
            Total descuento automático:{' '}
            <strong className="text-orange-600">
              ${totales.descuentoAuto.toLocaleString('es-CL')}
            </strong>
          </div>
        </Card>
      )}

      {/* ── Resumen total de ahorro (si hay cualquier tipo de ahorro) ── */}
      {!esGratis && tieneAhorro && (
        <Card className="mb-4 bg-blue-50 border-blue-200">
          <div className="space-y-2">
            <div className="flex justify-between items-center text-sm text-gray-500">
              <span>Precio particular:</span>
              <span className="line-through">${precioParticular.toLocaleString('es-CL')}</span>
            </div>

            {totales.esAdultoMayor && totales.tieneValorAdultoMayor && (
              <div className="flex justify-between items-center text-sm text-purple-600">
                <span>Tarifa adulto mayor:</span>
                <span>${totales.subtotal.toLocaleString('es-CL')}</span>
              </div>
            )}

            {totales.descuentoAuto > 0 && (
              <div className="flex justify-between items-center text-sm text-orange-600">
                <span>Descuentos automáticos:</span>
                <span>-${totales.descuentoAuto.toLocaleString('es-CL')}</span>
              </div>
            )}

            {totales.descuentoVecino > 0 && (
              <div className="flex justify-between items-center text-sm text-blue-600">
                <span>Descuento vecino:</span>
                <span>-${totales.descuentoVecino.toLocaleString('es-CL')}</span>
              </div>
            )}

            <Divider className="my-2" />

            <div className="flex justify-between items-center">
              <span className="font-semibold">Ahorro total:</span>
              <span className="text-xl font-bold text-blue-600">
                -${ahorroTotal.toLocaleString('es-CL')}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-lg font-bold">Total a pagar:</span>
              <span className={`text-2xl font-bold ${
                totales.esAdultoMayor && totales.tieneValorAdultoMayor
                  ? 'text-purple-600'
                  : 'text-blue-600'
              }`}>
                ${totales.total.toLocaleString('es-CL')}
              </span>
            </div>
          </div>
        </Card>
      )}

      {/* ── Alertas informativas finales ── */}
      {esGratis ? (
        <Alert
          message="Confirmación de inscripción"
          description="Al confirmar, quedarás inscrito en los horarios seleccionados de este taller gratuito."
          type="success"
          showIcon
          icon={<GiftOutlined />}
          className="mb-4"
        />
      ) : (
        <Alert
          message="Reserva temporal"
          description="Al agregar al carrito, tendrás 15 minutos para completar el pago. Los cupos quedarán reservados durante este tiempo."
          type="info"
          showIcon
          className="mb-4"
        />
      )}

      <div className="flex justify-between">
        <Button onClick={onPrev} size="large">Anterior</Button>
        <Button
          type="primary"
          size="large"
          className="btn-cta"
          icon={esGratis ? <CheckCircleOutlined /> : <ShoppingCartOutlined />}
          onClick={() => form.submit()}
          loading={loading || validandoTV}
          disabled={validandoTV}
        >
          {validandoTV ? 'Validando descuentos…' : (esGratis ? 'Confirmar inscripción' : 'Agregar al carrito')}
        </Button>
      </div>

      <Form form={form} onFinish={onSubmit} style={{ display: 'none' }} />
    </div>
  );
}
